/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Palette, createTheme } from '@mui/material';
import IconSVGCheckboxChecked from '@/shared/assets/images/icons/icon_checkbox_checked.svg?react';
import IconSVGCheckbox from '@/shared/assets/images/icons/icon_checkbox_unchecked.svg?react';

import { initialColors, paletteColors } from './vivicaUiKitColors';

type MainColorTypeKeys = keyof Pick<Palette, 'primary' | 'secondary'>;
export const muiTheme = createTheme({
  spacing: 1,
  shape: {
    borderRadius: 0,
  },
  header: {
    height: {
      xs: 64,
      sm: 64,
      md: 64,
      lg: 60,
    },
  },
  boxShadowTooltip: `2px 2px 6px 1px ${paletteColors.shadow}, -2px -2px 6px 1px ${paletteColors.shadow}`,
  pageTitleHeight: 42,
  typography: {
    fontFamily: 'Raleway, RalewayLocal, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '36px',
    },
    h2: {
      fontSize: '24px',
      fontWeight: 700,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 600,
    },
    body1: {
      fontSize: '20px',
      fontWeight: 400,
    },
    body2: {
      fontSize: '18px',
      fontWeight: 400,
    },
    '10_14_600': {
      fontSize: '10px',
      lineHeight: '14px',
      fontWeight: 600,
    },
    '12_16_600': {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 600,
    },
    '12_16_700': {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 700,
    },
    '12_18_500': {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 500,
    },
    '12_18_700': {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 700,
    },
    '12_20_500': {
      fontSize: '12px',
      lineHeight: '20px',
      fontWeight: 500,
    },
    '12_20_600': {
      fontSize: '12px',
      lineHeight: '20px',
      fontWeight: 600,
    },
    '12_22_500': {
      fontSize: '12px',
      lineHeight: '22px',
      fontWeight: 500,
    },
    '12_24_500': {
      fontSize: '12px',
      lineHeight: '24px',
      fontWeight: 500,
    },
    '14_16_500': {
      fontSize: '14px',
      lineHeight: '16px',
      fontWeight: 500,
    },
    '14_18_400': {
      fontSize: '14px',
      lineHeight: '18px',
      fontWeight: 400,
    },
    '14_18_500': {
      fontSize: '14px',
      lineHeight: '18px',
      fontWeight: 500,
    },
    '14_18_600': {
      fontSize: '14px',
      lineHeight: '18px',
      fontWeight: 600,
    },
    '14_18_700': {
      fontSize: '14px',
      lineHeight: '18px',
      fontWeight: 700,
    },
    '14_22_500': {
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 500,
    },
    '14_24_500': {
      fontSize: '14px',
      lineHeight: '24px',
      fontWeight: 500,
    },
    '14_24_700': {
      fontSize: '14px',
      lineHeight: '24px',
      fontWeight: 700,
    },
    '16_24_400': {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 400,
    },
    '18_28_500': {
      fontSize: '18px',
      lineHeight: '28px',
      fontWeight: 500,
    },
    '18_30_500': {
      fontSize: '18px',
      lineHeight: '30px',
      fontWeight: 500,
    },
    '20_24_500': {
      fontSize: '20px',
      lineHeight: '24px',
      fontWeight: 500,
    },
    '22_26_400': {
      fontSize: '22px',
      lineHeight: '26px',
      fontWeight: 400,
    },
    '22_30_500': {
      fontSize: '22px',
      lineHeight: '30px',
      fontWeight: 500,
    },
    '22_34_500': {
      fontSize: '22px',
      lineHeight: '34px',
      fontWeight: 500,
    },
    '24_28_400': {
      fontSize: '24px',
      lineHeight: '28px',
      fontWeight: 400,
    },
    '24_28_600': {
      fontSize: '24px',
      lineHeight: '28px',
      fontWeight: 600,
    },
    '24_30_500': {
      fontSize: '24px',
      lineHeight: '30px',
      fontWeight: 500,
    },
    '24_32_700': {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 700,
    },
    '24_34_500': {
      fontSize: '24px',
      lineHeight: '34px',
      fontWeight: 500,
    },
    '24_34_700': {
      fontSize: '24px',
      lineHeight: '34px',
      fontWeight: 700,
    },
    '24_36_700': {
      fontSize: '24px',
      lineHeight: '36px',
      fontWeight: 700,
    },
    '32_40_700': {
      fontSize: '32px',
      lineHeight: '40px',
      fontWeight: 700,
    },
    '32_42_700': {
      fontSize: '32px',
      lineHeight: '42px',
      fontWeight: 700,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          textTransform: 'initial',
          boxShadow: 'none',
          transition: 'none',
          minWidth: 120,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          fontWeight: 500,
          '&:hover, &:focus': {
            boxShadow: 'none',
          },
          '.MuiButton-startIcon, .MuiButton-endIcon': {
            width: 24,
            height: 24,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '.MuiButton-endIcon': {
            marginLeft: 6,
          },
          '.MuiButton-startIcon': {
            marginRight: 6,
          },
          [theme.breakpoints.down('lg')]: {
            '&.MuiButton-text': {
              minWidth: 'auto',
            },
          },
          ...(ownerState.variant === 'text' && {
            '&:hover': {
              background: 'transparent',
            },
            '&.Mui-disabled': {
              color: theme.palette.grey[300],
              'svg path': {
                fill: theme.palette.grey[300],
              },
            },
            'svg path': {
              fill: theme.palette[ownerState.color as MainColorTypeKeys]?.main,
            },
            '@media (hover: hover)': {
              '&:hover': {
                color:
                  theme.palette[ownerState.color as MainColorTypeKeys]?.light,
                path: {
                  fill: theme.palette[ownerState.color as MainColorTypeKeys]
                    ?.light,
                },
              },
            },
            '&:active': {
              color: theme.palette[ownerState.color as MainColorTypeKeys]?.dark,
              path: {
                fill: theme.palette[ownerState.color as MainColorTypeKeys]
                  ?.dark,
              },
            },
          }),
          ...(ownerState.variant === 'outlined' && {
            color: theme.palette[ownerState.color as MainColorTypeKeys]?.main,
            borderColor:
              theme.palette[ownerState.color as MainColorTypeKeys]?.main,
            'svg path': {
              fill: theme.palette[ownerState.color as MainColorTypeKeys]?.main,
            },
            '&.Mui-disabled': {
              color: theme.palette.grey[300],
              borderColor: theme.palette.grey[300],
              'svg path': {
                fill: theme.palette.grey[300],
              },
            },
            '@media (hover: hover)': {
              '&:hover': {
                color:
                  theme.palette[ownerState.color as MainColorTypeKeys]?.light,
                borderColor:
                  theme.palette[ownerState.color as MainColorTypeKeys]?.light,
                'svg path': {
                  fill: theme.palette[ownerState.color as MainColorTypeKeys]
                    ?.light,
                },
                background: 'transparent',
              },
            },
            '&:active': {
              color: theme.palette[ownerState.color as MainColorTypeKeys]?.dark,
              borderColor:
                theme.palette[ownerState.color as MainColorTypeKeys]?.dark,
              'svg path': {
                fill: theme.palette[ownerState.color as MainColorTypeKeys]
                  ?.dark,
              },
            },
          }),
          ...(ownerState.variant === 'contained' && {
            '&.Mui-disabled': {
              color: theme.palette.grey[300],
              'svg path': {
                fill: theme.palette.grey[300],
              },
              background: theme.palette.grey[200],
            },
            ...(ownerState.color === 'primary' && {
              '@media (hover: hover)': {
                '&:hover': {
                  background: theme.palette.primary.light,
                  boxShadow: 'none',
                },
              },
              '&:active': {
                background: theme.palette.primary.dark,
              },
            }),
            ...(ownerState.color === 'secondary' && {
              '@media (hover: hover)': {
                '&:hover': {
                  background: theme.palette.secondary.light,
                  boxShadow: 'none',
                },
              },
              '&:active': {
                background: theme.palette.secondary.dark,
              },
            }),
          }),
        }),
        sizeSmall: {
          height: 32,
        },
        sizeLarge: {
          height: 54,
        },
        sizeMedium: {
          height: 42,
          padding: '6px 12px',
        },
        startIcon: {
          marginLeft: 0,
        },
      },
      defaultProps: {
        variant: 'contained',
        disableRipple: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: 'transparent',
          },
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
    MuiDialog: {
      defaultProps: {
        PaperProps: {
          sx: {
            backgroundColor: 'white',
            boxShadow: 'none',
            borderRadius: 0,
            '.mobile-layout &': {
              marginLeft: '24px',
              marginRight: '24px',
              margin: 0,
            },
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        style: { fontFamily: 'inherit' },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            background: 'none',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: () => ({
          boxSizing: 'border-box',
          backgroundColor: '#fff',
          borderRadius: 0,
          color: paletteColors.grey['500'],
          '&:hover': {
            backgroundColor: '#fff',
          },
          '&.Mui-focused': {
            backgroundColor: '#fff',
          },
          '&.Mui-disabled': {
            background: 'none',
          },
          '& > div': {
            padding: '6px 0 6px 0',
          },
          '& > div > div ': {
            color: '#abb3bd',
          },
          '& .MuiInputBase-root ': {
            height: 56,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: paletteColors.grey[800],
          margin: 0,
          ...(ownerState.labelPlacement === 'top' && {
            alignItems: 'flex-start',
          }),
        }),
        label: () => ({
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '20px',
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: () => ({
          fontSize: '14px',
          color: paletteColors.grey['500'],
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: { margin: '0 2px 0 0' },
        listbox: () => ({
          padding: 0,
          border: '1px solid #DEE9ED',
          maxHeight: '200px',
          overflow: 'auto',
          '& > li': {
            fontSize: '14px',
            fontWeight: 400,
            '&.MuiAutocomplete-option': {
              paddingTop: 0,
              paddingBottom: 0,
            },
            '&.MuiAutocomplete-option[aria-selected="true"]': {
              background: 'none',
            },
            '&.MuiAutocomplete-option[aria-selected="true"].Mui-focused,&.MuiAutocomplete-option:hover':
              {
                color: initialColors.secondary,
                background: '#EEF5F8 ',
              },
          },
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <IconSVGCheckbox />,
        checkedIcon: <IconSVGCheckboxChecked />,
      },
      styleOverrides: {
        root: {
          '&:hover': {
            background: 'none',
          },
        },
      },
    },
  },
});
